import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics, featuredComicIds, hotComicIds, latestComicIds, siteInfo, faqs } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const comicMap = new Map<string, Comic>();
comics.forEach((c) => comicMap.set(c.id, c));

const featuredComics = featuredComicIds.map((id) => comicMap.get(id)).filter(Boolean) as Comic[];
const hotComics = hotComicIds.map((id) => comicMap.get(id)).filter(Boolean) as Comic[];
const latestComics = latestComicIds.map((id) => comicMap.get(id)).filter(Boolean) as Comic[];

const homeSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51漫画网',
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51漫画网',
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '51漫画网 - 免费韩国漫画在线阅读平台',
    description: siteInfo.description,
    url: siteInfo.url,
    isPartOf: {
      '@type': 'WebSite',
      name: '51漫画网',
      url: siteInfo.url,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  },
];

export default function Home() {
  return (
    <>
      <SEO
        title="韩国漫画无删减漫画下拉式-免费韩漫大全-51漫画网"
        description={siteInfo.description}
        keywords="韩国漫画,无删减漫画,下拉式漫画,免费韩漫大全,51漫画网,韩漫"
        canonicalUrl={siteInfo.url}
        schemaData={homeSchema}
      />

      <Navbar />
      <ScrollToTop />

      <main>
        {/* Hero Section */}
        <section className="relative h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20illustration%2C%20artistic%20abstract%20comic%20panel%20background%20with%20warm%20coral%20and%20amber%20gradient%20tones%2C%20soft%20geometric%20shapes%2C%20dreamy%20reading%20atmosphere%2C%20clean%20modern%20design%2C%20no%20text&width=1600&height=700&seq=manhwa-hero-bg&orientation=landscape"
            alt="51漫画网 - 韩国漫画阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-[1280px] mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 mb-4 leading-tight">
              {siteInfo.name}
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
              {siteInfo.slogan}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rexue-dongzuo"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                立即阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-semibold rounded-full text-sm backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-10">
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  {siteInfo.totalComics.toLocaleString()}+
                </p>
                <p className="text-xs text-background-300 mt-1">海量漫画</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  {(siteInfo.totalUsers / 10000).toFixed(0)}万+
                </p>
                <p className="text-xs text-background-300 mt-1">忠实用户</p>
              </div>
              <div className="w-px h-10 bg-background-50/20"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  {(siteInfo.dailyVisitors / 10000).toFixed(1)}万+
                </p>
                <p className="text-xs text-background-300 mt-1">日访问量</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-500 mt-2">多种题材，总有一款适合你</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 bg-background-100 hover:bg-primary-50 rounded-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-foreground-600 group-hover:text-primary-500 text-xl transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Comics */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  编辑推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">精选高品质韩漫，不容错过</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="large" />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  热门漫画
                </h2>
                <p className="text-sm text-foreground-500 mt-1">千万读者共同选择，口碑之选</p>
              </div>
              <Link
                to="/category/rexue-dongzuo"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">实时追踪最新话，追更不等待</p>
              </div>
              <Link
                to="/category/rexue-dongzuo"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium whitespace-nowrap"
              >
                更多更新
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                为什么选择51漫画网
              </h2>
              <p className="text-sm text-foreground-500 mt-2">专注韩漫阅读，用心做好每一个细节</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-read-line', title: '海量韩漫资源', desc: '收录1000+部韩国漫画，涵盖各类题材，满足不同读者的口味需求。' },
                { icon: 'ri-download-line', title: '无删减阅读', desc: '提供原汁原味的无删减内容，让你享受最完整的韩漫阅读体验。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '适配手机端的下拉式阅读模式，操作简单流畅，沉浸感十足。' },
                { icon: 'ri-flashlight-line', title: '更新及时', desc: '与韩漫原作同步更新，让你第一时间读到最新内容。' },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center text-center p-6 bg-background-100 rounded-xl">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-50 mb-4">
                    <i className={`${item.icon} text-primary-500 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-2">解答你关于韩漫阅读的所有疑问</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20warm%20reading%20atmosphere%2C%20cozy%20cafe%20with%20comic%20books%2C%20soft%20warm%20coral%20lighting%2C%20artistic%20abstract%20background%2C%20clean%20modern%20design%2C%20no%20text&width=1600&height=500&seq=manhwa-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/60 to-foreground-900/70"></div>
          <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
              准备好开始阅读了吗？
            </h2>
            <p className="text-sm md:text-base text-background-200 mb-8 max-w-xl mx-auto">
              即刻加入51漫画网，畅享无删减韩国漫画下拉式阅读体验
            </p>
            <Link
              to="/category/rexue-dongzuo"
              className="inline-flex px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              开始免费阅读
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}